import { FileText, LayoutDashboard, LogOut, Settings } from "lucide-react";
import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import { PreferenceControls } from "@/components/app-settings/preference-controls";
import { useAppSettings } from "@/components/app-settings/app-settings-provider";
import { useAuth } from "@/components/auth/auth-provider";
import { Button } from "@/components/ui/button";
import { resolveAdminLogoSource } from "@/lib/branding/image-source";
import { workbenchRoutes } from "@/lib/routes/workbench-routes";
import { cn } from "@/lib/utils/cn";

const navItems = [
  { to: workbenchRoutes.dashboard, labelKey: "nav.dashboard", icon: LayoutDashboard },
  { to: workbenchRoutes.quotations, labelKey: "nav.quotations", icon: FileText },
  { to: workbenchRoutes.settings, labelKey: "nav.settings", icon: Settings, adminOnly: true },
] as const;

export function AdminLayout() {
  const settings = useAppSettings();
  const { theme, headerSubtitle, headerTitle, t } = settings;
  const { user, logout } = useAuth();
  const location = useLocation();
  const isQuoteEditor = location.pathname.startsWith(`${workbenchRoutes.quotations}/`) && location.pathname.endsWith("/edit");
  const visibleNavItems = navItems.filter((item) => !("adminOnly" in item && item.adminOnly) || user?.role === "admin");
  const logoSrc = resolveAdminLogoSource(settings, theme);
  const brand = (
    <div className="flex min-w-0 items-center gap-3">
      <SidebarBrandImage key={logoSrc} src={logoSrc} />
      <div className="min-w-0 [overflow-wrap:anywhere]">
        <div className="text-base font-semibold leading-6">{headerTitle || t("app.name")}</div>
        <div className="mt-0.5 text-xs leading-5 text-muted-foreground">{headerSubtitle || t("app.subtitle")}</div>
      </div>
    </div>
  );

  return (
    <div className="workbench-shell min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 hidden w-60 overflow-y-auto border-r border-border bg-muted/40 px-3 py-6 lg:block">
        <div className="px-2 pb-7">{brand}</div>
        <nav aria-label={t("app.name")} className="space-y-1">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;

            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    "flex min-h-10 items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
                    isActive && "bg-primary/10 text-primary hover:bg-primary/10 hover:text-primary",
                  )
                }
              >
                <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                {t(item.labelKey)}
              </NavLink>
            );
          })}
        </nav>
      </aside>
      <div className="border-b border-border px-4 py-4 lg:hidden">{brand}</div>
      <nav aria-label={t("app.name")} className="sticky top-0 z-30 flex gap-1 border-b border-border bg-background px-3 py-2 lg:hidden">
        {visibleNavItems.map((item) => {
          const Icon = item.icon;

          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => cn(
                "flex min-h-11 min-w-0 flex-1 items-center justify-center gap-2 rounded-md px-2 py-2 text-sm font-medium leading-5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
                isActive && "bg-primary/10 text-primary hover:bg-primary/10 hover:text-primary",
              )}
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{t(item.labelKey)}</span>
            </NavLink>
          );
        })}
      </nav>
      <main className="min-w-0 lg:pl-60">
        <div className={cn("mx-auto px-4 py-5 md:px-6", isQuoteEditor ? "max-w-none 2xl:px-8" : "max-w-7xl")}>
          <header className="mb-6 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-b border-border pb-4">
            {user ? (
              <div className="min-w-0 basis-full text-xs leading-5 text-muted-foreground [overflow-wrap:anywhere] sm:flex-1 sm:basis-auto">
                <div className="font-medium text-foreground">{user.name}</div>
                <div>{user.jobTitle ? `${user.jobTitle} · ` : ""}{user.email}</div>
              </div>
            ) : null}
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <PreferenceControls />
              <Button type="button" variant="secondary" className="h-8 px-2" onClick={() => void logout()} title={t("auth.logout")}>
                <LogOut className="h-4 w-4" aria-hidden="true" />
                {t("auth.logout")}
              </Button>
            </div>
          </header>
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function SidebarBrandImage({ src }: { src: string | null }) {
  const [failed, setFailed] = useState(false);

  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center text-primary">
      {src && !failed ? (
        <img src={src} alt="" referrerPolicy="no-referrer" className="h-9 w-9 object-contain" onError={() => setFailed(true)} />
      ) : (
        <FileText className="h-6 w-6" aria-hidden="true" />
      )}
    </span>
  );
}
